import * as React from "react";
import { UitkPrimaryFloatingButton, UitkButtonSize } from "uitk-react-button";
import { UitkDialogContent, UitkFullscreenDialog, UitkDialogAnimationType } from "uitk-react-dialog";
import { UitkRadioGroup, UitkRadio } from "uitk-react-switch";
import { UitkText } from "uitk-react-text";
import { UitkToolbar, ToolbarType } from "uitk-react-toolbar";

import { useLocalization } from "@shared-ui/localization-context";

import { locKeys } from "components/flexComponents/WizardCruisePWA/l10n";

export enum LOCATION_TYPES {
  origin = "ORIGIN",
  destination = "DESTINATION",
}

export interface LocationsListOption {
  code: string;
  name: string;
}

export interface LocationsListGroup extends LocationsListOption {
  items: LocationsListOption[];
}

export interface Locations3PPDialogProps {
  queryRouteId: string;
  locationType?: LOCATION_TYPES;
  toolbarHeader: string;
  options: LocationsListOption[];
  onDismiss: () => void;
  onDoneButtonClick: (option: LocationsListOption) => void;
}

export const Locations3PPDialog: React.FC<Locations3PPDialogProps> = (props) => {
  const { locationType = LOCATION_TYPES.origin, options } = props;
  const { formatText } = useLocalization();

  const [currentCode, setCurrentCode] = React.useState("");
  const [currentName, setCurrentName] = React.useState("");

  const handleDoneButtonClick = () => {
    props.onDoneButtonClick({ code: currentCode, name: currentName });
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: code, labels } = event.currentTarget;
    const [label] = labels;

    setCurrentCode(code);
    setCurrentName(label.textContent as string);
  };

  /**
   * Destinations can be grouped by country however, there's a limitation from the API
   * where a single array is received.
   * Transform array of options to destination groups for easier rendering
   * @param {LocationsListOption[]} destinations Original options from service call
   */
  const optionsAsDestinationGroups = (destinations: LocationsListOption[]) => {
    const destinationGroups: LocationsListGroup[] = [];

    destinations.forEach(({ name, code }) => {
      const isCountry = !name.includes(" - ");

      if (isCountry) {
        const items = [{ name, code }];

        destinationGroups.push({ name, code, items });
      } else {
        destinationGroups[destinationGroups.length - 1].items.push({ name: name.replace(" - ", ""), code });
      }
    });

    return destinationGroups;
  };

  return (
    <UitkFullscreenDialog dialogShow={true} animation={UitkDialogAnimationType.SLIDEUP}>
      <UitkToolbar
        action={props.onDismiss}
        iconLabel={formatText(locKeys.dismissDialog)}
        type={ToolbarType.CLOSE}
        header={props.toolbarHeader}
      />

      <UitkDialogContent zeroPadding={false} className="all-l-margin-two">
        {locationType === LOCATION_TYPES.origin && (
          <UitkRadioGroup group={`${props.queryRouteId}-group`} onChange={handleRadioChange} selected={currentCode}>
            {props.options.map(({ code, name }) => (
              <UitkRadio
                key={`${props.queryRouteId}-radio-${code}`}
                id={`${props.queryRouteId}-radio-${code}`}
                value={code}
                label={name}
              />
            ))}
          </UitkRadioGroup>
        )}

        {locationType === LOCATION_TYPES.destination &&
          optionsAsDestinationGroups(options).map((group) => {
            return (
              <section key={group.name} className="all-y-padding-three">
                <UitkText size={400} weight="bold" className="all-y-padding-three">
                  {group.name}
                </UitkText>

                <UitkRadioGroup
                  group={`${props.queryRouteId}-group`}
                  onChange={handleRadioChange}
                  selected={currentCode}
                >
                  {group.items.map(({ code, name }: LocationsListOption) => (
                    <UitkRadio
                      key={`${props.queryRouteId}-radio-${code}`}
                      id={`${props.queryRouteId}-radio-${code}`}
                      value={code}
                      label={name}
                    />
                  ))}
                </UitkRadioGroup>
              </section>
            );
          })}
      </UitkDialogContent>

      <footer className="all-x-padding-three all-y-padding-three">
        <UitkPrimaryFloatingButton
          onClick={handleDoneButtonClick}
          className={`${props.queryRouteId}-confirm-button`}
          text={formatText(locKeys.dialogDoneButton)}
          subText={currentName}
          size={UitkButtonSize.LARGE}
          isFullWidth={true}
        />
      </footer>
    </UitkFullscreenDialog>
  );
};
